import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import routes from "routes.js";
import { COLORS } from "constant/adminTheme";
import TinyPageTabs from "./TinyPageTabs";
import "./TinyPageHeader.css";

/**
 * Tiny 後臺的頁面標題區（公版）
 *
 * # 它裝什麼
 *
 *   身分   圖示 + 標題 + 狀態標記 + 一段說明      「這是什麼頁」
 *   範圍   scope —— 期間 / 週期 / 期數選擇器      「在看哪一份資料」
 *   視角   tabs  —— 期數明細 / 趨勢總覽 …         「同一份資料的哪個切面」
 *
 * 🚫 **不裝動作**：儲存 / 匯出 / 立即同步 / 套用調整 —— 那些要放在它們作用的
 * 那一區旁邊。判準：按下去會**改變資料**的是動作，只**改變你看到什麼**的才是
 * 範圍或視角。
 *
 * 🚫 **也不裝同頁並存的 filter**（例如金流數據頁的幣別／粒度／單位三組）。
 * 那種是「資料區的控制列」，屬於資料區。
 *
 * 這條線很重要：目前 23 個 Tiny 頁面的 header 之所以四頁四種形狀，就是因為
 * 每頁都在裡面「順便」放了那頁剛好需要的東西。有明確的收容標準，才不會回頭。
 *
 * # 版型
 *
 * `variant` a/b/c/d，四種排列共用同一份 DOM，只換 CSS —— 所以切換版型
 * **不會重新掛載子元件**（週期選擇器不會閃、不會掉狀態）。細節見同名 .css。
 *
 * # 圖示：預設就是側欄那顆，不用傳
 *
 * `routes.js` 每一頁本來就有 `icon`（側欄在用），所以這裡**沒傳 icon 就自動
 * 拿同一顆** —— 標題與側欄永遠一致，而且新頁面零成本就對。
 *
 * 🔴 這條規則同時修掉一個既有的 bug：`nucleo-icons.css` **只有 @font-face、
 * 沒有任何一個 `.nc-*` glyph class**（實測 0 筆），所以 repo 裡那 18 處
 * `nc-icon nc-xxx` **全部都是看不見的** —— 雙週獎池的標題與兩個 tab 圖示
 * 就是這樣消失的。routes.js 那批全是 `fa`，是實際會顯示的那一套。
 *
 * # scope 放週期選擇器時的兩個必要 prop
 *
 * `<TinyWeekSelector>` 的預設值是 `label="週期"` 與 `showMeta={true}` ——
 * 它們會在選擇器**上下各多一行**（標籤、「共 N 周期 / 第 i / N」）。
 * 那是給頁面內容區用的完整型態，放進 header 會把 scope 區撐高兩行，
 * 於是同一個公版 header 在不同頁高度就不一樣（Brad 2026-09-10 實際看出來的：
 * 每週交收總覽比雙週獎池 / EcoBot 高一截）。
 *
 * 🔑 **進 header 的一律 `label="" showMeta={{false}}`**，靠 header 自己的
 * 標題與 `--tph-scope-h` 決定高度。
 *
 * # 色票
 *
 * 一律走 `constant/adminTheme`，由這裡寫成 CSS 變數餵給 .css。
 * 實測各頁本地 COLORS 與 adminTheme **零衝突**（兩邊都有的 token 值完全相同），
 * 所以換過來不會變色。
 * 🚫 不要再在頁面裡抄第三份色票 —— 這個 repo 已經因為「靠複製維持一致性」
 * 漂過四次（`getWeekExpenses`、`CashflowEditCell`、下載檔案、這批 header）。
 */

const TONES = {
  accent:  { bg: "rgba(102,126,234,0.18)", border: "rgba(102,126,234,0.5)",  text: "#a3b0ff" },
  success: { bg: "rgba(45,206,137,0.16)",  border: "rgba(45,206,137,0.45)",  text: "#4fd69c" },
  danger:  { bg: "rgba(245,54,92,0.16)",   border: "rgba(245,54,92,0.45)",   text: "#f97b96" },
  warning: { bg: "rgba(255,141,114,0.16)", border: "rgba(255,141,114,0.45)", text: "#ffa98f" },
  info:    { bg: "rgba(29,140,248,0.16)",  border: "rgba(29,140,248,0.45)",  text: "#6cb6fb" },
  muted:   { bg: "rgba(255,255,255,0.08)", border: "rgba(255,255,255,0.18)", text: COLORS.textSecondary },
};

const HeaderBadge = ({ text, tone }) => {
  const t = TONES[tone] || TONES.accent;
  return (
    <span
      style={{
        display: "inline-block",
        marginLeft: 10,
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.text,
      }}
    >
      {text}
    </span>
  );
};

// 色票 → CSS 變數。只在這裡做一次，.css 全部用 var()
const CSS_VARS = {
  "--tph-card-bg": COLORS.cardBg,
  "--tph-border": COLORS.border,
  "--tph-border-soft": "rgba(255,255,255,0.07)",
  "--tph-text": COLORS.textPrimary,
  "--tph-text-muted": COLORS.textMuted,
  "--tph-text-dim": COLORS.textDimmed,
};

/** 用目前路徑回頭查 routes.js 的側欄圖示。查不到就不畫，不猜。 */
function useRouteIcon() {
  const { pathname } = useLocation();
  return React.useMemo(() => {
    let best = null;
    for (const r of routes) {
      const full = (r.layout || "") + r.path;
      if (pathname === full) return r.icon || null;
      // 巢狀路徑取最長前綴 —— 不能用 indexOf，那會讓 /admin/tiny-* 全部
      // 命中 /admin/tiny（Admin.js 的 getBrandText 就是這樣壞掉的）
      if (pathname.startsWith(full + "/") && (!best || full.length > best.len)) {
        best = { icon: r.icon || null, len: full.length };
      }
    }
    return best ? best.icon : null;
  }, [pathname]);
}

function TinyPageHeader({
  variant = "a",
  icon,
  title,
  badges,
  description,
  meta,
  scope,
  reserveScope = false,
  actions,
  tabs,
}) {
  const routeIcon = useRouteIcon();
  const shownIcon = icon || routeIcon;
  const list = (badges || []).filter(Boolean);
  const hasTabs = !!(tabs && tabs.items && tabs.items.length);

  const tabsEl = hasTabs ? (
    <TinyPageTabs
      items={tabs.items}
      active={tabs.active}
      onChange={tabs.onChange}
      bare={variant !== "a"}
      look={tabs.look}
    />
  ) : null;

  return (
    <>
      <div className="tph" data-variant={variant} style={CSS_VARS}>
        <div className="tph__main">
          <div className="tph__identity">
            <h4 className="tph__title">
              {shownIcon && (
                <i className={shownIcon} style={{ color: `var(--tph-nav-active, ${COLORS.navActive})`, marginRight: 10 }} />
              )}
              {title}
              {list.map((b, i) => (
                <HeaderBadge key={i} text={b.text} tone={b.tone} />
              ))}
            </h4>
            {description && <p className="tph__desc">{description}</p>}
          </div>

          {/* ⚠️ `reserveScope` 的頁面即使 scope 是 null 也要把 aside 畫出來 ——
                否則切到沒有選擇器的視角時，這一列整個不見、header 變矮、
                底下的 tab 會跟著跳。高度由 CSS 的 --tph-scope-h 鎖住。 */}
          {(meta || scope || actions || reserveScope || (hasTabs && (variant === "b" || variant === "d"))) && (
            <div className="tph__aside">
              {meta && <div className="tph__meta">{meta}</div>}
              {actions && <div className="tph__actions">{actions}</div>}
              {scope && <div className="tph__scope">{scope}</div>}
              {/* b（三段同列）與 d（右側堆疊）把 tabs 收在右側區 */}
              {hasTabs && (variant === "b" || variant === "d") && tabsEl}
            </div>
          )}
        </div>

        {/* c：tabs 貼在同一張卡的底部，中間一條分隔線 */}
        {hasTabs && variant === "c" && <div className="tph__tabs-row">{tabsEl}</div>}
      </div>

      {/* a：tabs 自成一張卡，在標題卡外面 */}
      {hasTabs && variant === "a" && tabsEl}
    </>
  );
}

TinyPageHeader.propTypes = {
  /** a=標題列/tab列分開　b=三段同列　c=tabs貼底部　d=右側堆疊 */
  variant: PropTypes.oneOf(["a", "b", "c", "d"]),
  /**
   * 圖示的**完整 className**（例如 "fa fa-trophy"）。
   * ⚠️ **通常不用傳** —— 不傳就自動用 `routes.js` 裡側欄那顆，標題與側欄
   *    因此永遠一致。只有要刻意跟側欄不同時才傳。
   * 🚫 不要傳 `nc-icon nc-*`：那套 glyph class 在這個專案根本沒被定義
   *    （`nucleo-icons.css` 只有 @font-face），畫出來是空的。
   */
  icon: PropTypes.string,
  title: PropTypes.node.isRequired,
  /** 純文字狀態標記；tone 見 TONES，預設 accent */
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.node.isRequired,
      tone: PropTypes.oneOf(Object.keys(TONES)),
    })
  ),
  description: PropTypes.node,
  /** 右上角次要資訊，**只放字串**（如 club id） */
  meta: PropTypes.node,
  /** 範圍控制 —— 期間 / 週期 / 期數選擇器，只此一類（見檔頭 🚫 清單） */
  scope: PropTypes.node,
  /**
   * 標題列右側的**頁面層級動作**（例如「計分規則」那顆開 modal 的鈕）。
   *
   * 🔑 跟 `scope` 分開是刻意的：`scope` 換的是「哪一份資料」，`actions` 是
   *    「對這一頁做一件事」。混在一起的話，選擇器與按鈕會在同一格裡搶寬度，
   *    而且讀的人分不出哪個會改變畫面上的數字。
   * 🚫 只放**與整頁有關**的動作。作用在某張表 / 某個 tab 上的（匯出這張表、
   *    新增一筆到這張列表）要跟那張表放在一起 —— 那是 `TinyPageTabs` 的
   *    `right` 槽或卡片標題列的工作。
   * ⚠️ 最多一到兩顆，而且要小顆（link / sm）—— 這裡不是主要動作區。
   */
  actions: PropTypes.node,
  /**
   * `scope` **只在某些視角出現**的頁面要設 true（例如雙週獎池的期數選擇器
   * 只在「期數明細」有、切到「趨勢總覽」就沒有）。設了之後即使 scope 是 null
   * 也會保留那一列的高度，**header 高度固定不隨視角變動**。
   * 🚫 從頭到尾都沒有 scope 的頁面不要設 —— 會白留一列空白。
   */
  reserveScope: PropTypes.bool,
  /** 子視圖切換。給 { items, active, onChange }，排哪裡由 variant 決定 */
  tabs: PropTypes.shape({
    items: PropTypes.array,
    active: PropTypes.string,
    onChange: PropTypes.func,
    /** underline（預設）/ pill —— 見 TinyPageTabs */
    look: PropTypes.oneOf(["underline", "pill"]),
  }),
};

export default TinyPageHeader;
